import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { ThemedSegmented } from "@/shared/components/ThemedSegmented";
import { cn } from "@/shared/lib/utils";

const NAVY = "#120240";

const exportCategories = [
  { key: "patient",       label: "Patient Demographics",     icon: "lucide:user-round",     description: "Name, DOB, address, contact info",                fhirType: "Patient"            },
  { key: "conditions",    label: "Conditions & Diagnoses",   icon: "lucide:stethoscope",    description: "Active and historical conditions",                 fhirType: "Condition"          },
  { key: "medications",   label: "Medications",              icon: "lucide:pill",           description: "Current and past medications with RxNorm codes",   fhirType: "MedicationRequest"  },
  { key: "labs",          label: "Lab Results",              icon: "lucide:test-tube",      description: "Laboratory results with LOINC codes",              fhirType: "Observation"        },
  { key: "vitals",        label: "Vital Signs",              icon: "lucide:heart-pulse",    description: "Blood pressure, weight, height, BMI",              fhirType: "Observation"        },
  { key: "immunizations", label: "Immunizations",            icon: "lucide:syringe",        description: "Vaccination history",                             fhirType: "Immunization"       },
  { key: "allergies",     label: "Allergies & Intolerances", icon: "lucide:triangle-alert", description: "Drug and food allergies",                          fhirType: "AllergyIntolerance" },
  { key: "procedures",    label: "Procedures",               icon: "lucide:history",        description: "Surgeries and medical procedures",                 fhirType: "Procedure"          },
  { key: "careplans",     label: "Care Plans",               icon: "lucide:clipboard-list", description: "Treatment plans and goals",                        fhirType: "CarePlan"           },
  { key: "documents",     label: "Clinical Documents",       icon: "lucide:file-text",      description: "Clinical notes and reports",                       fhirType: "DocumentReference"  },
  { key: "imaging",       label: "Imaging Studies",          icon: "lucide:scan-line",      description: "Radiology reports",                                fhirType: "ImagingStudy"       },
  { key: "encounters",    label: "Encounters",               icon: "lucide:calendar",       description: "Visit history",                                    fhirType: "Encounter"          },
];

const CARD = "rounded-[14px] border border-[#E5E7EB] bg-white";

const SMALL_SPLITTER =
  "!rounded-full !border !border-[#120240] [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-thumb]:!rounded-full " +
  "[&_.ant-segmented-item-label]:!min-h-0 [&_.ant-segmented-item-label]:!px-1.5 [&_.ant-segmented-item-label]:!text-[11px] " +
  "[&_.ant-segmented-item-label]:!font-bold [&_.ant-segmented-item-label]:!leading-[20px]";

function todayIso() {
  return new Date().toLocaleDateString("en-CA");
}

function DateBox({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div className="min-w-0">
      <p className="!mb-0 text-base text-[#6B7280]">{label}</p>
      {/* Calendar icon on the left (design); the picker's own right-hand icon is hidden. */}
      <div className="mt-2.5 flex h-[42px] items-center gap-4 rounded-[10px] border border-[#E5E7EB] bg-white px-4 [&_button_svg]:hidden hover:border-[#E4002B]/50">
        <Icon icon="lucide:calendar-days" className="h-4 w-4 shrink-0 text-[#120240]" />
        <div className="min-w-0 flex-1 [&_button]:!text-base [&_button_span]:!text-[#120240]">
          <CalendarDateField value={value} onChange={onChange} placeholder={placeholder} bare />
        </div>
      </div>
    </div>
  );
}

export function FHIRExportSection() {
  const [selectedCategories, setSelectedCategories] = useState<Record<string, boolean>>(
    exportCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: true }), {})
  );
  const [fromDate, setFromDate]         = useState("");
  const [toDate, setToDate]             = useState(todayIso);
  const [exportFormat, setExportFormat] = useState<"json" | "ndjson">("json");
  const [bundleType, setBundleType]     = useState<"collection" | "document">("collection");
  const [isExporting, setIsExporting]   = useState(false);

  const selectedCount = Object.values(selectedCategories).filter(Boolean).length;
  const allSelected = selectedCount === exportCategories.length;
  // One tag per FHIR resource type (Lab Results and Vital Signs are both Observation).
  const selectedResources = [...new Set(exportCategories.filter(c => selectedCategories[c.key]).map(c => c.fhirType))];

  const toggleCategory = (key: string) => {
    setSelectedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleAll = () => {
    setSelectedCategories(Object.fromEntries(exportCategories.map(c => [c.key, !allSelected])));
  };

  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 2000));
    setIsExporting(false);
  };

  return (
    <div className="min-h-full px-4 py-4">

      {/* Compliance banner */}
      <div className={cn(CARD, "mb-3 flex items-center gap-3 px-3 py-[11px]")}>
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F2889C]">
          <span aria-hidden="true" className="text-[34px] font-light leading-none text-white">?</span>
        </div>
        <div className="min-w-0">
          <p className="!mb-0 text-[15px] font-bold text-[#120240]">USCDI v3 Compliant Export</p>
          <p className="!mb-0 !mt-1 text-[12.5px] tracking-[-0.005em] text-[#4B5563]">
            Your export follows US Core Implementation Guide profiles and uses standard terminologies including RxNorm
            for medications, LOINC for lab results, and SNOMED-CT for conditions.
          </p>
        </div>
      </div>

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_377px]">

        {/* ── Left: select data ── */}
        <section className={cn(CARD, "p-3")}>
          <h2 className="!mb-0 text-[19px] font-extrabold text-[#120240]">Select Data to Export</h2>
          <p className="!mb-0 !mt-1.5 text-[15px] text-[#6B7280]">Choose which health record categories to include in your export</p>

          <div className="mt-6 grid gap-5 sm:grid-cols-2 sm:max-w-[568px]">
            <DateBox label="From" value={fromDate} onChange={setFromDate} placeholder="Start date" />
            <DateBox label="To" value={toDate} onChange={setToDate} placeholder="End date" />
          </div>

          <div className="mt-7 flex items-center justify-between gap-3">
            <p className="!mb-0 text-base font-semibold text-[#6B7280]">Clinical Domain:</p>
            <Button
              variant="link"
              onClick={toggleAll}
              className="!h-auto !p-0 !text-xs !font-semibold !text-[#120240] hover:!text-[#E4002B]"
            >
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
          </div>

          <div className="mt-4 grid gap-x-[11px] gap-y-[21px] sm:grid-cols-2 lg:grid-cols-3">
            {exportCategories.map((category) => {
              const isSelected = selectedCategories[category.key];
              return (
                <div
                  key={category.key}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={() => toggleCategory(category.key)}
                  onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggleCategory(category.key); } }}
                  className={cn(CARD, "flex min-h-[98px] cursor-pointer gap-2.5 py-3 pl-[11px] pr-2 transition-colors hover:bg-[#FFF9FA]")}
                >
                  {/* The card itself is the checkbox (role, keyboard); this is its visual box. */}
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border transition-colors",
                      isSelected ? "border-[#F2889C] bg-[#F2889C]" : "border-[#C9CBD0] bg-white",
                    )}
                  >
                    {isSelected && <Icon icon="lucide:check" className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F2889C]">
                    <Icon icon={category.icon} className="h-[18px] w-[18px] text-white" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="!mb-0 text-[13px] font-semibold leading-5 text-[#111827]">{category.label}</p>
                    <p className="!mb-0 text-[11px] leading-4 text-[#6B7280]">{category.description}</p>
                    <span className="mt-auto self-start rounded-full border border-[#9CA3AF] px-1.5 pt-px text-[10px] leading-[13px] text-[#6B7280]">
                      {category.fhirType}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Right: export summary ── */}
        <aside className={cn(CARD, "px-4 pb-4 pt-3.5 xl:sticky xl:top-5")}>
          <h2 className="!mb-0 text-[19px] font-extrabold text-[#120240]">Export Summary</h2>
          <p className="!mb-0 !mt-1 text-[15px] leading-[18px] text-[#6B7280]">Choose which health record categories to include in your export</p>

          <div className="mt-[22px] grid grid-cols-3 gap-3">
            {[
              { label: "Categories",  value: String(selectedCount) },
              { label: "Format",      value: exportFormat === "json" ? "JSON" : "NDJSON" },
              { label: "Bundle Type", value: bundleType === "collection" ? "Collection" : "Document" },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="!mb-0 text-[11px] text-[#6B7280]">{label}</p>
                <p className="!mb-0 !mt-3 text-[13px] font-bold text-[#120240]">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-[18px] flex flex-wrap gap-1">
            {selectedResources.length > 0 ? (
              selectedResources.map((type) => (
                <span key={type} className="rounded-full border border-[#F2889C] px-3 py-[5px] text-xs leading-4 text-[#F2889C]">
                  {type}
                </span>
              ))
            ) : (
              <p className="!mb-0 text-xs text-[#9CA3AF]">Select at least one category to export.</p>
            )}
          </div>

          <h3 className="!mb-0 !mt-[22px] text-[15px] font-bold text-[#120240]">Export option</h3>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <p className="!mb-0 text-[13px] text-[#6B7280]">Format</p>
              <ThemedSegmented
                selectedBg={NAVY}
                itemColor="#6B7280"
                trackPadding={2}
                value={exportFormat}
                onChange={(v) => setExportFormat(v as "json" | "ndjson")}
                options={[{ value: "json", label: "JSON Bundle" }, { value: "ndjson", label: "NDJSON (Bulk)" }]}
                className={cn("!mt-2", SMALL_SPLITTER)}
              />
            </div>
            <div>
              <p className="!mb-0 text-[13px] text-[#6B7280]">Bundle Type</p>
              <ThemedSegmented
                selectedBg={NAVY}
                itemColor="#6B7280"
                trackPadding={2}
                value={bundleType}
                onChange={(v) => setBundleType(v as "collection" | "document")}
                options={[{ value: "collection", label: "Collection" }, { value: "document", label: "Document" }]}
                className={cn("!mt-2", SMALL_SPLITTER)}
              />
            </div>
          </div>

          <Button
            onClick={handleExport}
            disabled={selectedCount === 0}
            loading={isExporting}
            className="!mt-[26px] !flex !h-[34px] !w-full !items-center !justify-center !gap-2 !rounded-[10px] !border-0 !bg-[#120240] !text-xs !font-semibold !text-white hover:!bg-[#120240] hover:!text-white hover:!opacity-90 disabled:!opacity-50"
          >
            {!isExporting && <Icon icon="lucide:download" className="h-4 w-4 !text-white" />}
            Export FHIR Bundle
          </Button>
        </aside>
      </div>
    </div>
  );
}
